
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { CreateElement } from "vue";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import BrandMixin from "@/mixins/brandMixin";
import BillingCyclesStore from "@/mixins/constants/billingCyclesStore";
import HasValidation from "@/mixins/HasValidation";

// Enums/Data
import { Inheritance } from "@/data/enums/priceLists";

// Types
import { IPricelist, IProductPrice } from "@/models/products";
import { IBillingCycle, ICurrency } from "@/models/constants";

@Component({
  components: {
    "select-billing-cycle": () =>
      import(
        "@/components/app/admin/catalogue/products/billing/selectBillingCycle.vue"
      )
  }
})
export default class ProductPriceMatrix extends Mixins(
  RequestPromises,
  BrandMixin,
  BillingCyclesStore,
  HasValidation
) {
  // Props
  @Prop({ required: true })
  public value!: any[];

  @Prop({ required: true })
  public productPrices!: IProductPrice[];

  @Prop({ required: true })
  public pricelist!: IPricelist;

  @Prop({ required: true })
  public isRecurring!: boolean;

  @Prop({ default: false })
  public isDisabled!: boolean;

  @Prop({ required: true })
  public usedCurrencies!: ICurrency[];

  @Prop({ required: true })
  public usedTerms!: number[];

  @Prop({ required: true })
  public priceErrors!: any[];

  @Prop({ default: "" })
  public parentPriceListName!: string;

  @Prop({ default: false })
  public useYearsOnly!: boolean;

  @Prop({ default: true })
  public canAddTerm!: boolean;

  @Prop({ default: false })
  public canAddCurrency!: boolean;

  @Prop({ default: true })
  public canUpdateTerm!: boolean;

  @Prop({ default: true })
  public showErrors!: boolean;

  // Data

  public isLoading = true;
  public isReloading = false;
  public pricesForm = {};
  public requestError = false;
  public selectedField: string | null = null;
  public initiated = false;

  // Lifecycle methods
  public async created() {
    this.loadData();
  }

  public async loadData() {
    try {
      await Promise.all([this.getBillingCycles()]);
      this.initFormPrices();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    }
    this.isLoading = false;
    this.isReloading = false;
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public initFormPrices() {
    const blankForm = {};
    _.each(this.pricedTerms, term => {
      _.each(this.pricedCurrencies, currency => {
        _.set(blankForm, `${currency.code}_${term}`, null);
      });
    });
    this.pricesForm = _.merge(blankForm, this.initialPrices);
  }

  // Render Function
  public render(h: CreateElement) {
    if (this.isLoading)
      return h("is-loading", {
        props: { if: true }
      });

    if (this.requestError)
      return h("has-request-error", {
        props: {
          hasError: true,
          isLoading: this.isReloading
        },
        attrs: { isAbsolute: true },
        on: {
          retry: this.reloadData
        }
      });

    return h("div", {}, [
      h("div", { class: "prices-wrapper" }, [
        h("div", { class: "price-block price-block-currencies" }, [
          h("div", { class: "has-margin-bottom-100 has-padding-top-25" }, [
            this.$slots.switcher
          ]),
          h(
            "div",
            {},
            _.concat(
              _.map(this.pricedCurrencies, (currency: ICurrency) => {
                return this.createCurrencyCell(h, currency);
              }),
              this.isDefaultPricelist || this.canAddCurrency
                ? [
                    h(
                      "static-field",
                      {
                        props: {
                          action: true,
                          iconType: "is-info"
                        },
                        on: {
                          click: () => {
                            this.$emit("addCurrency");
                          }
                        }
                      },
                      [
                        h("p", {}, [
                          h(
                            "a",
                            { class: "is-underlined is-info" },
                            this.$t("_.add_currency").toString()
                          )
                        ])
                      ]
                    )
                  ]
                : []
            )
          )
        ]),
        h(
          "div",
          { class: "prices-content" },
          _.concat(
            _.map(this.pricedTerms, (term: IBillingCycle["months"]) => {
              return h(
                "div",
                {
                  class: "price-block",
                  props: {
                    term: term,
                    key: `t_${term}`
                  }
                },
                [
                  this.createTermCell(h, term),
                  h(
                    "div",
                    {
                      class: "price-block-body"
                    },
                    _.concat(
                      _.map(this.pricedCurrencies, (currency: ICurrency) => {
                        const formKey = `${currency.code}_${term}`;
                        const formVal = _.get(this.pricesForm, formKey, null);
                        const inheritedVal = _.get(
                          this.inheritedPrices,
                          formKey,
                          null
                        );

                        // Set new price - important - don't remove
                        this.$set(
                          this.pricesForm,
                          formKey,
                          _.get(this.pricesForm, formKey, null)
                        );

                        return h(
                          "div",
                          {
                            class: "pricing-block-price"
                          },
                          [
                            this[
                              this.selectedField === formKey
                                ? "createRealInput"
                                : "createFauxInput"
                            ](h, currency, formKey, formVal, inheritedVal)
                          ]
                        );
                      }),
                      this.isDefaultPricelist || this.canAddCurrency
                        ? [
                            h(
                              "div",
                              {
                                class: "is-ghost"
                              },
                              [h("p", {}, [h("span", {}, "-")])]
                            )
                          ]
                        : []
                    )
                  )
                ]
              );
            }),
            this.canAddTerm ? this.createAddTermBlock(h) : []
          )
        )
      ])
    ]);
  }

  public createFauxInput(
    h: CreateElement,
    currency: ICurrency,
    formKey: string,
    formVal: number | null,
    inheritedVal: number | null
  ) {
    const isInherited = _.isNumber(inheritedVal) && _.isNull(formVal);
    const inputVal = _.isNumber(formVal) ? formVal : inheritedVal;
    const hasValue = _.isNumber(inputVal);

    const hasError =
      this.requiredPriceKeys.includes(formKey) ||
      !!_.get(this.priceErrors, formKey, []).length;

    return h(
      "p",
      {
        class: _.compact([
          "control",
          "input",
          "has-cursor-text",
          !hasValue && "has-text-grey-light",
          hasError && "is-requiring-price",
          isInherited && "is-inherited"
        ]).join(" "),
        key: formKey,
        attrs: {
          tabindex: `0`
        },
        on: {
          click: () => (this.selectedField = formKey),
          focus: () => (this.selectedField = formKey)
        }
      },
      [
        this.formatValue(inputVal, currency),
        this.createInputIcon(h, formKey, hasError, isInherited)
      ]
    );
  }

  public createRealInput(
    h: CreateElement,
    currency: ICurrency,
    formKey: string,
    formVal: number | null,
    inheritedVal: number | null
  ) {
    formVal = _.isNumber(formVal) ? formVal : inheritedVal;
    const hasError =
      this.requiredPriceKeys.includes(formKey) ||
      !!_.get(this.priceErrors, formKey, []).length;
    const isInherited = _.isNumber(inheritedVal) && _.isNull(formVal);
    return h(
      "u-field",
      {
        class: _.compact([
          "pricing-block-price",
          hasError && "is-requiring-price"
        ]).join(" "),
        props: {
          vid: formKey,
          vname: this.$t("_.amount")
            .toString()
            .toLowerCase(),
          showErrorMessage: false,
          rules: `${
            this.requiredPriceKeys.includes(formKey) ? "required|" : ""
          }min_value:0`,
          expanded: true
        }
      },
      [
        h(
          "currency-input",
          {
            key: formKey,
            props: {
              currency,
              min: 0,
              showCode: false,
              showErrorMessage: false,
              inputId: formKey,
              value: formVal
            },
            on: {
              input: value => {
                _.set(this.pricesForm, formKey, value);
                setTimeout(() => {
                  this.$set(
                    this.priceErrors,
                    formKey,
                    this.validationErrors[formKey]
                  );

                  if (!this.requiredPriceKeys.length) {
                    _.forEach(
                      this.priceErrors,
                      (priceErrorValue, priceErrorKey) => {
                        if (
                          _.some(
                            priceErrorValue,
                            value => value.indexOf("is required") !== -1
                          )
                        ) {
                          this.$set(
                            this.priceErrors,
                            priceErrorKey,
                            _.filter(
                              this.priceErrors[priceErrorKey],
                              errorValue =>
                                errorValue.indexOf("is required") === -1
                            )
                          );
                        }
                      }
                    );
                  } else {
                    _.forEach(this.requiredPriceKeys, requiredKey => {
                      if (
                        (!_.get(this.priceErrors, requiredKey, []).length ||
                          _.some(
                            _.get(this.priceErrors, requiredKey, []),
                            value => value.indexOf("is required") === -1
                          )) &&
                        this.pricesForm[requiredKey] === null
                      ) {
                        this.$set(
                          this.priceErrors,
                          requiredKey,
                          _.concat(_.get(this.priceErrors, requiredKey, []), [
                            this.$t("_sentence.amount_field_required")
                          ])
                        );
                      }
                    });
                  }
                }, 50);
              },
              blur: () => (this.selectedField = null),
              created: () => this.onCreated(formKey)
            },
            directives: [
              {
                name: "focus",
                value: true
              }
            ]
          },
          [this.createInputIcon(h, formKey, hasError, isInherited)]
        )
      ]
    );
  }

  public createCurrencyCell(h: CreateElement, currency: ICurrency) {
    return h(
      "static-field",
      {
        class: "price-currency",
        props: { key: `currency-${currency.id}` },
        attrs: { isAnimated: false }
      },
      [
        h("div", { class: "is-flex has-items-aligned-center" }, [
          h("currency-flag", { props: { currencyCode: currency.code } }),
          h("p", { class: "has-margin-left-50" }, [
            currency.code,
            h(
              "span",
              { class: "has-text-grey-light" },
              `(${currency.prefix || currency.suffix})`
            )
          ]),
          h(
            "span",
            { class: "pull-flex-end" },
            this.brandCurrencyId === currency.id
              ? [
                  h(
                    "b-tooltip",
                    {
                      attrs: {
                        label: this.$t("_.your_brand_default_currency"),
                        type: "is-primary",
                        position: "is-left"
                      }
                    },
                    [
                      h(
                        "b-icon",
                        {
                          class: "has-text-primary",
                          attrs: { icon: "star", size: "is-small" }
                        },
                        []
                      )
                    ]
                  )
                ]
              : this.isDefaultPricelist || this.canAddCurrency
              ? [
                  h(
                    "a",
                    {
                      on: {
                        click: event => {
                          event.preventDefault();
                          event.stopPropagation();

                          this.$emit("removeCurrency", currency.id);
                        }
                      }
                    },
                    [
                      h("b-icon", {
                        class: "has-text-danger",
                        props: {
                          icon: "times",
                          size: "is-small"
                        }
                      })
                    ]
                  )
                ]
              : []
          )
        ])
      ]
    );
  }

  public createTermCell(h: CreateElement, term: number) {
    return h(
      "div",
      {
        class: "has-margin-bottom-100"
      },
      [
        h("div", {}, [
          h("p", {}, [
            this.isRecurring && this.isDefaultPricelist && this.canUpdateTerm
              ? h("select-billing-cycle", {
                  attrs: {
                    canDelete: true
                  },
                  props: {
                    usedTermsIds: this.pricedTerms,
                    usedTermsId: term,
                    useYearsOnly: this.useYearsOnly
                  },
                  scopedSlots: {
                    trigger: props =>
                      h("a", { class: "has-text-dark" }, [
                        h(
                          "span",
                          { class: "is-underlined has-margin-right-25" },
                          term
                            ? this.$tc(
                                "_plurals.every_object_months",
                                term
                              ).toString()
                            : this.$t("_.price").toString()
                        ),
                        h(
                          "b-icon",
                          {
                            attrs: { size: "is-small" },
                            props: {
                              icon: props.termActive
                                ? "chevron-down"
                                : "chevron-right"
                            }
                          },
                          []
                        )
                      ])
                  },
                  on: {
                    deleteTerm: () => this.$emit("removeTerm", term),
                    newTerm: newTerm => this.$emit("updateTerm", term, newTerm)
                  }
                })
              : h(
                  "span",
                  {},
                  term
                    ? this.$tc("_plurals.every_object_months", term).toString()
                    : this.$t("_.price").toString()
                )
          ]),
          h("p", { class: "has-text-grey-light" }, [
            h("small", {}, [h("em", {}, this.getTermSubname(term))])
          ])
        ])
      ]
    );
  }

  public createInputIcon(h: CreateElement, formKey, hasError, isInherited) {
    if (hasError) {
      return h("b-icon", {
        class: "has-icon-right",
        props: {
          icon: "exclamation-triangle",
          size: "is-small",
          type: "is-danger"
        }
      });
    }

    if (this.missingPriceKeys.includes(formKey)) {
      return h("b-icon", {
        class: "has-icon-right",
        props: {
          icon: "exclamation-triangle",
          size: "is-small",
          type: "is-warning"
        }
      });
    }

    if (isInherited) {
      return h(
        "b-tooltip",
        {
          class: "has-icon-right",
          props: {
            label: this.$t("_.inherited_price_desc", {
              name: this.parentPriceListName
            }),
            type: "is-dark",
            multilined: true
          }
        },
        [
          h("b-icon", {
            props: {
              icon: "link",
              size: "is-small",
              type: "is-grey-light"
            }
          })
        ]
      );
    }

    return "";
  }

  public createAddTermBlock(h: CreateElement) {
    return this.isDefaultPricelist && this.isRecurring
      ? [
          h("div", { class: "price-block" }, [
            h("div", { class: "has-margin-bottom-100" }, [
              h("div", {}, [
                h("select-billing-cycle", {
                  props: {
                    usedTermsIds: this.pricedTerms,
                    iconType: "is-info",
                    useYearsOnly: this.useYearsOnly
                  },
                  on: {
                    newTerm: newTerm => this.$emit("addTerm", newTerm)
                  },
                  scopedSlots: {
                    trigger: props =>
                      h("a", {}, [
                        h(
                          "span",
                          {
                            class:
                              "is-underlined has-margin-right-25 has-text-primary"
                          },
                          this.$t("_.add_new_term").toString()
                        ),
                        h(
                          "b-icon",
                          {
                            attrs: { size: "is-small" },
                            props: {
                              icon: props.termActive
                                ? "chevron-down"
                                : "chevron-right"
                            }
                          },
                          []
                        )
                      ])
                  }
                }),
                h("p", { class: "has-text-grey-light" }, "-")
              ])
            ]),
            h("div", {}, [
              h(
                "div",
                {},
                _.concat(
                  _.map(this.pricedCurrencies, (currency: ICurrency) => {
                    return h(
                      "div",
                      {
                        class: "is-ghost",
                        props: { key: `ghost_${currency.id}` }
                      },
                      [
                        h("p", {}, [
                          h("span", {}, `${currency.prefix}-${currency.suffix}`)
                        ])
                      ]
                    );
                  }),
                  [
                    h(
                      "div",
                      {
                        class: "is-ghost"
                      },
                      [h("p", {}, [h("span", {}, "-")])]
                    )
                  ]
                )
              )
            ])
          ])
        ]
      : [];
  }

  public formatValue(value, currency) {
    const prefix = _.get(currency, "prefix");
    const suffix = _.get(currency, "suffix");
    const regex = /(\d)(?=(\d{3})+(?:\.\d+)?$)/g;
    const floatVal = parseFloat(value);
    return [
      prefix,
      isNaN(floatVal) ? " - " : floatVal.toFixed(2).replace(regex, "$1,"),
      suffix
    ]
      .join("")
      .trim();
  }

  public onCreated(formKey: string) {
    setTimeout(() => {
      this.$set(
        this.observer.errors,
        formKey,
        _.get(this.priceErrors, formKey, [])
      );
    }, 50);
  }

  public get pricedCurrencies() {
    return _.sortBy(this.usedCurrencies, [
      (c: ICurrency) => c.id !== this.brandCurrencyId,
      "code"
    ]);
  }

  public get pricedTerms() {
    return _.orderBy(this.usedTerms);
  }

  public get currenciesByCode() {
    return _.keyBy(this.usedCurrencies, "code");
  }

  public get isDefaultPricelist() {
    return this.pricelist.id === this.brandPriceListId;
  }

  public get isInheritingPricelist() {
    return this.pricelist.inheritance === Inheritance.INHERITED;
  }

  public get keyedPrices() {
    return _.keyBy(
      _.filter(this.productPrices, ["pricelist_id", this.pricelist.id]),
      price => `${price.currency_code}_${price.billing_cycle_months}`
    );
  }

  public get initialPrices() {
    const initial = _.pickBy(this.keyedPrices, ["own_price", true]);
    return _.mapValues(initial, priceObj => priceObj.price);
  }

  public get inheritedPrices() {
    const inherited = _.pickBy(this.keyedPrices, ["own_price", false]);
    return _.mapValues(inherited, priceObj => priceObj.price);
  }

  public get missingPriceKeys(): string[] {
    if (!this.isDefaultPricelist || !this.showErrors) return [];

    return _.keys(_.pickBy(this.pricesForm, _.isNull));
  }

  public get requiredPriceKeys(): string[] {
    if (this.isInheritingPricelist || !this.showErrors) return [];

    return _.keys(
      _.pickBy(this.pricesForm, (price, key) => {
        const [code, term] = key.split("_");
        const isBrandCurrency = code === this.brandCurrencyCode;

        return (
          isBrandCurrency &&
          _.isNull(price) &&
          !!this.currentFormPrices.length &&
          _.some(this.usedCurrencies, ["code", code]) &&
          this.usedTerms.includes(_.toNumber(term)) &&
          _.some(
            this.currentFormPrices,
            price =>
              price.billing_cycle_months === _.toNumber(term) &&
              _.get(this.currenciesByCode, `[${code}].id`, "") !==
                price.currency_id
          )
        );
      })
    );
  }

  public get currentFormPrices() {
    return _.filter(
      _.compact(
        _.map(this.pricesForm, (price, key) => {
          const [code, term] = key.split("_");
          if (typeof price !== "number") return;
          return {
            billing_cycle_months: parseInt(term),
            currency_id: _.get(this.currenciesByCode, `[${code}].id`, ""),
            price,
            pricelist_id: this.pricelist.id
          };
        })
      ),
      compactedPrice => {
        return (
          _.some(this.usedCurrencies, ["id", compactedPrice.currency_id]) &&
          this.usedTerms.includes(compactedPrice.billing_cycle_months)
        );
      }
    );
  }

  public getTermSubname(term) {
    return _.get(_.find(this.billingCycles, ["months", term]), "name", "-");
  }

  public changeTermPrices(oldTerm, newTerm) {
    _.forEach(this.pricesForm, (priceValue, priceKey) => {
      const [code, term] = priceKey.split("_");
      if (term === oldTerm.toString()) {
        delete this.pricesForm[priceKey];
        this.$set(this.pricesForm, `${code}_${newTerm}`, priceValue);
      }
    });
  }

  @Watch("currentFormPrices", { deep: true })
  public onCurrentFormPricesChange() {
    this.$emit("input", this.currentFormPrices);

    if (this.initiated) return;
    this.initiated = true;
    this.$emit("initiateFormPrices");
  }
}
